import { cn } from '../../lib/utils';
import { AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { useEffect, useState } from 'react';
import { services } from '../../services';
import { useNavigate } from 'react-router-dom';

interface Alert {
  id: string;
  type: 'warning' | 'pending' | 'success' | 'error';
  title: string;
  description: string;
  action?: string;
  actionUrl?: string;
}

const iconMap = {
  warning: AlertTriangle,
  pending: Clock,
  success: CheckCircle,
  error: XCircle,
};

const colorMap = {
  warning: 'bg-warning/10 border-warning/30 text-warning',
  pending: 'bg-info/10 border-info/30 text-info',
  success: 'bg-success/10 border-success/30 text-success',
  error: 'bg-destructive/10 border-destructive/30 text-destructive',
};

interface AlertCenterProps {
  className?: string;
}

export function AlertCenter({ className }: AlertCenterProps) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
    // Refresh alerts every 60 seconds
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: Array<{
          _id?: string;
          id?: string;
          type: string;
          title: string;
          description: string;
          action?: string;
          actionUrl?: string;
        }>;
      }>('/admin/dashboard/alerts');

      if (response.status === 200 && response.data?.status && response.data?.data) {
        const formattedAlerts = response.data.data.map((alert) => ({
          id: alert._id || alert.id || '',
          type: (alert.type || 'warning') as Alert['type'],
          title: alert.title || 'Alert',
          description: alert.description || '',
          action: alert.action,
          actionUrl: alert.actionUrl,
        }));
        setAlerts(formattedAlerts);
      } else if (response.status === 401) {
        console.error('Authentication required for alerts');
      } else if (response.status === 403) {
        console.error('Admin access required for alerts');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: unknown } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required for alerts');
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required for alerts');
      } else {
        console.error('Error fetching alerts:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (alert: Alert) => {
    if (alert.actionUrl) {
      navigate(alert.actionUrl);
    }
  };

  return (
    <div className={cn('glass-card rounded-xl p-6 animate-slide-up', className)}>
      <h3 className='text-lg font-semibold text-foreground mb-4'>Alert Center</h3>
      <div className='space-y-3'>
        {loading ? (
          <div className='p-8 text-center text-muted-foreground'>
            <p>Loading alerts...</p>
          </div>
        ) : alerts.length === 0 ? (
          <div className='p-8 text-center text-muted-foreground'>
            <CheckCircle className='w-10 h-10 mx-auto mb-3 opacity-50' />
            <p>No alerts</p>
          </div>
        ) : (
          alerts.map((alert, index) => {
            const Icon = iconMap[alert.type];
            return (
              <div
                key={alert.id || `alert-${index}`}
                className={cn('flex items-start gap-3 p-4 rounded-lg border', colorMap[alert.type])}
              >
                <Icon className='w-5 h-5 shrink-0 mt-0.5' />
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium'>{alert.title}</p>
                  <p className='text-xs opacity-80 mt-0.5'>{alert.description}</p>
                </div>
                {alert.action && (
                  <Button
                    size='sm'
                    variant='ghost'
                    className='shrink-0 text-xs'
                    onClick={() => handleAction(alert)}
                  >
                    {alert.action}
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
